import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Plus, X, ChevronLeft, ChevronRight, ChevronDown, ArrowUpCircle, ArrowDownCircle, Shield, ShieldCheck, MessageSquare, Info, MoveUp } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { isOCCSymbol } from '../lib/occParser'
import AccountName from './AccountName'
import PrivateValue from './PrivateValue'

/* ── Formatters ───────────────────────────────────────────────────────────── */

function fmtCurrency(val) {
  if (val == null) return '—'
  return Number(val).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtTimeCT(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
    hour12: true,
  })
}

function fmtDateShort(ts) {
  if (!ts) return ''
  return new Date(ts).toLocaleDateString('en-US', {
    timeZone: 'America/Chicago',
    month: 'short', day: 'numeric',
  })
}

/* ── Event type config ────────────────────────────────────────────────────── */

const EVENT_COLORS = {
  buy:  { dot: 'bg-forest', border: 'border-forest/50', ring: 'ring-forest/40', badge: 'bg-forest/10 text-forest border-forest/30' },
  sell: { dot: 'bg-ember',  border: 'border-ember/50',  ring: 'ring-ember/40',  badge: 'bg-ember/10 text-ember border-ember/30' },
  stop: { dot: 'bg-[#c97f2a]', border: 'border-[#c97f2a]/50', ring: 'ring-[#c97f2a]/40', badge: 'bg-[#c97f2a]/10 text-[#c97f2a] border-[#c97f2a]/30' },
  note: { dot: 'bg-ink/40', border: 'border-ink/20', ring: 'ring-ink/20', badge: 'bg-canvas2 text-inkMuted border-border' },
}

function getEventColor(evt) {
  if (evt.type === 'note') return EVENT_COLORS.note
  if (evt.type === 'stop') return EVENT_COLORS.stop
  if (evt.side === 'buy') return EVENT_COLORS.buy
  return EVENT_COLORS.sell
}

function getEventLabel(evt) {
  if (evt.type === 'note') return 'NOTE'
  if (evt.type === 'stop') return evt.previousPrice == null ? 'STOP' : 'STOP ↑'
  return evt.side?.toUpperCase() || '—'
}

function getEventIcon(evt) {
  if (evt.type === 'note') return MessageSquare
  if (evt.type === 'stop') {
    // Shield+ for a brand-new stop (no prior price), Shield for a moved stop
    return evt.previousPrice == null ? ShieldCheck : Shield
  }
  if (evt.side === 'buy') return ArrowUpCircle
  return ArrowDownCircle
}

/* ── Consolidation: merge multi-account events into single markers ────── */

const CONSOLIDATION_WINDOW_MS = 15 * 60 * 1000 // 15 minutes — Schwab fills across accounts can spread out
const PRICE_THRESHOLD = 0.03 // 3%

function consolidateEvents(events) {
  if (events.length === 0) return []

  const groups = []
  let current = null

  for (const evt of events) {
    const time = new Date(evt.timestamp).getTime()
    const price = Number(evt.price) || 0
    // Build a grouping key: type + side
    const groupKey = evt.type === 'execution'
      ? `exec-${evt.side}-${evt.executionType || ''}`
      : evt.type === 'stop' ? (evt.previousPrice == null ? 'stop-added' : 'stop-moved')
      : null // notes never consolidate

    const canMerge = current
      && groupKey
      && current.groupKey === groupKey
      && time - current.earliestTime <= CONSOLIDATION_WINDOW_MS
      && (price === 0 || current.avgPrice === 0 || Math.abs(price - current.avgPrice) / current.avgPrice <= PRICE_THRESHOLD)

    if (canMerge) {
      current.children.push(evt)
      current.latestTime = Math.max(current.latestTime, time)
      current.totalQty += Number(evt.quantity) || 0
      if (price > 0) {
        current.priceSum += price
        current.priceCount++
        current.avgPrice = current.priceSum / current.priceCount
      }
    } else {
      current = {
        groupKey,
        children: [evt],
        earliestTime: time,
        latestTime: time,
        totalQty: Number(evt.quantity) || 0,
        priceSum: price > 0 ? price : 0,
        priceCount: price > 0 ? 1 : 0,
        avgPrice: price,
      }
      groups.push(current)
    }
  }

  // Build consolidated event objects
  return groups.map(g => {
    const first = g.children[0]
    const count = g.children.length
    const isConsolidated = count > 1

    return {
      ...first,
      // Override with consolidated data
      _consolidated: isConsolidated,
      _count: count,
      _children: g.children,
      _totalQty: g.totalQty,
      _avgPrice: g.avgPrice,
      // Use earliest timestamp for positioning
      timestamp: first.timestamp,
      quantity: g.totalQty,
      price: g.avgPrice,
    }
  })
}

/* ── Positioning: proportional with minimum pixel spacing ─────────────── */

const EDGE_PAD = 24
const MIN_GAP = 36
const DOT_SIZE = 20

function computePositions(events, containerWidth) {
  if (events.length === 0 || containerWidth <= 0) return []

  if (events.length === 1) {
    const usable = containerWidth - 2 * EDGE_PAD
    return [{ ...events[0], px: EDGE_PAD + usable / 2, idx: 0, _totalWidth: containerWidth }]
  }

  const times = events.map(e => new Date(e.timestamp).getTime())
  const minT = Math.min(...times)
  const maxT = Math.max(...times)
  const range = maxT - minT || 1

  // First pass: proportional positions within the container
  const usable = containerWidth - 2 * EDGE_PAD
  const items = events.map((evt, i) => {
    const t = new Date(evt.timestamp).getTime()
    const ratio = (t - minT) / range
    const px = Math.round(EDGE_PAD + ratio * usable)
    return { ...evt, px, idx: i, _totalWidth: containerWidth }
  })

  // Second pass: enforce minimum spacing, pushing right as needed
  for (let i = 1; i < items.length; i++) {
    if (items[i].px - items[i - 1].px < MIN_GAP) {
      items[i].px = items[i - 1].px + MIN_GAP
    }
  }

  // If enforcement pushed dots beyond the container, expand and
  // redistribute proportionally across the new wider track so gaps
  // stay proportional instead of cascading from the left.
  const lastPx = items[items.length - 1].px + EDGE_PAD
  if (lastPx > containerWidth) {
    const totalWidth = lastPx
    const newUsable = totalWidth - 2 * EDGE_PAD

    // Redistribute: map the enforced positions into the wider space
    // while preserving their relative ordering and minimum gaps
    const firstPx = items[0].px
    const oldSpan = items[items.length - 1].px - firstPx || 1
    for (const item of items) {
      item.px = Math.round(EDGE_PAD + ((item.px - firstPx) / oldSpan) * newUsable)
      item._totalWidth = totalWidth
    }

    // Re-enforce minimums after redistribution
    for (let i = 1; i < items.length; i++) {
      if (items[i].px - items[i - 1].px < MIN_GAP) {
        items[i].px = items[i - 1].px + MIN_GAP
      }
    }

    const finalWidth = Math.max(totalWidth, items[items.length - 1].px + EDGE_PAD)
    for (const item of items) item._totalWidth = finalWidth
  }

  return items
}

/* ── Hover Tooltip — shows consolidated account breakdown ───────────── */

function HoverTooltip({ evt, anchorRect, isOption }) {
  if (!anchorRect) return null

  const left = anchorRect.left + anchorRect.width / 2
  const top = anchorRect.top - 8
  const colors = getEventColor(evt)
  const children = evt._children || [evt]
  const isConsolidated = evt._consolidated

  return createPortal(
    <div
      className="fixed z-[9999] pointer-events-none"
      style={{ left, top, transform: 'translate(-50%, -100%)' }}
    >
      <div className="bg-canvas2 border border-border rounded-lg shadow-lg px-3 py-2 max-w-[280px]">
        {/* Header: badge + count + time */}
        <div className="flex items-center gap-1.5 mb-1">
          <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded font-semibold border ${colors.badge}`}>
            {getEventLabel(evt)}
          </span>
          {isConsolidated && (
            <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-full bg-canvas border border-border text-inkMuted">
              ×{evt._count}
            </span>
          )}
          <span className="font-mono text-[9px] text-inkMuted">{fmtTimeCT(evt.timestamp)}</span>
        </div>

        {/* Aggregate line */}
        {evt.type === 'execution' && (
          <div className="font-mono text-[10px] text-ink mb-1">
            <PrivateValue>{Math.round(evt._totalQty || evt.quantity)} {isOption ? 'contracts' : 'shares'} @ {fmtCurrency(evt._avgPrice || evt.price)}</PrivateValue>
          </div>
        )}
        {evt.type === 'stop' && (
          <div className="font-mono text-[10px] text-ink mb-1">
            {evt.previousPrice != null && <><PrivateValue>{fmtCurrency(evt.previousPrice)}</PrivateValue> → </>}
            <PrivateValue>{fmtCurrency(evt.price)}</PrivateValue>
          </div>
        )}
        {evt.type === 'note' && evt.note && (
          <div className="font-mono text-[10px] text-ink truncate">{evt.note.length > 50 ? evt.note.slice(0, 50) + '...' : evt.note}</div>
        )}

        {/* Per-account breakdown */}
        {isConsolidated && (
          <div className="mt-1.5 pt-1.5 border-t border-border space-y-0.5">
            {children.map((child, i) => (
              <div key={i} className="flex items-center justify-between gap-3">
                <AccountName accountNumber={child.accountNumber} className="font-mono text-[9px] text-inkMuted" />
                <span className="font-mono text-[9px] text-inkMuted tabular-nums">
                  {child.type === 'execution' && <PrivateValue>{Math.round(Number(child.quantity))} {isOption ? 'contracts' : 'shares'} @ {fmtCurrency(child.price)}</PrivateValue>}
                  {child.type === 'stop' && <PrivateValue>{child.previousPrice != null ? `${fmtCurrency(child.previousPrice)} → ` : ''}{fmtCurrency(child.price)}</PrivateValue>}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Single event — show account + details */}
        {!isConsolidated && evt.accountNumber && (
          <div className="flex items-center justify-between gap-3 mt-0.5">
            <AccountName accountNumber={evt.accountNumber} className="font-mono text-[9px] text-inkMuted" />
            {evt.type === 'execution' && (
              <span className="font-mono text-[9px] text-inkMuted tabular-nums">
                <PrivateValue>{Math.round(Number(evt.quantity))} {isOption ? 'contracts' : 'shares'}</PrivateValue>
              </span>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

/* ── Detail Slide-Over Panel ────────────────────────────────────────────── */

function EventDetailPanel({ evt, currentIdx, totalCount, onClose, onNavigate, onSaveNote, saving, isOption }) {
  const [noteText, setNoteText] = useState(evt.note || '')
  const [editingNote, setEditingNote] = useState(false)
  const children = evt._children || [evt]
  const isConsolidated = evt._consolidated

  useEffect(() => {
    setNoteText(evt.note || '')
    setEditingNote(false)
  }, [evt.id, currentIdx])

  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft' && currentIdx > 0) onNavigate(currentIdx - 1)
      if (e.key === 'ArrowRight' && currentIdx < totalCount - 1) onNavigate(currentIdx + 1)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose, onNavigate, currentIdx, totalCount])

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  const colors = getEventColor(evt)
  const hasPrev = currentIdx > 0
  const hasNext = currentIdx < totalCount - 1

  return createPortal(
    <div className="fixed inset-0 z-[9998] flex justify-end">
      <div
        className="absolute inset-0 transition-opacity duration-300"
        style={{ background: 'rgba(0, 0, 0, 0.35)' }}
        onClick={onClose}
      />

      <div
        className="relative w-full sm:w-[380px] sm:max-w-[90vw] h-full flex flex-col animate-slide-in-right"
        style={{ background: 'rgb(var(--color-canvas))', borderLeft: '1px solid rgb(var(--color-border))' }}
      >
        {/* Header */}
        <div className="px-5 pt-4 pb-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => hasPrev && onNavigate(currentIdx - 1)}
                disabled={!hasPrev}
                className="p-1 rounded-lg transition-colors hover:bg-canvas2 disabled:opacity-25 disabled:cursor-default"
              >
                <ChevronLeft size={14} className="text-inkMuted" />
              </button>
              <span className="font-mono text-[10px] text-inkMuted tabular-nums">
                {currentIdx + 1} of {totalCount}
              </span>
              <button
                onClick={() => hasNext && onNavigate(currentIdx + 1)}
                disabled={!hasNext}
                className="p-1 rounded-lg transition-colors hover:bg-canvas2 disabled:opacity-25 disabled:cursor-default"
              >
                <ChevronRight size={14} className="text-inkMuted" />
              </button>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg transition-colors hover:bg-canvas2">
              <X size={14} className="text-inkMuted" />
            </button>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <span className={`font-mono text-[10px] px-2.5 py-1 rounded-full font-semibold border ${colors.badge}`}>
              {getEventLabel(evt)}
            </span>
            {isConsolidated && (
              <span className="font-mono text-[10px] px-2 py-0.5 rounded-full border bg-canvas2 text-inkMuted border-border">
                {evt._count} accounts
              </span>
            )}
            {evt.type === 'execution' && (
              <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full border ${
                evt.executionType === 'entry' ? 'bg-forest/10 text-forest border-forest/30' : 'bg-ember/10 text-ember border-ember/30'
              }`}>
                {evt.executionType === 'entry' ? 'ENTRY' : evt.executionType === 'exit' ? 'EXIT' : evt.executionType?.replace('_', ' ').toUpperCase()}
              </span>
            )}
          </div>

          <div className="font-mono text-xs text-inkMuted">{fmtTimeCT(evt.timestamp)}</div>
        </div>

        {/* Details */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

          {/* Aggregate summary */}
          {evt.type === 'execution' && (
            <div>
              <div className="font-mono text-[9px] tracking-widest uppercase text-inkMuted mb-2">
                {isConsolidated ? 'Total Execution' : 'Execution Details'}
              </div>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="font-mono text-xs text-inkMuted">{isOption ? 'Contracts' : 'Total Shares'}</span>
                  <span className="font-mono text-xs text-ink">{Math.round(evt._totalQty || evt.quantity)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-mono text-xs text-inkMuted">Avg Price</span>
                  <span className="font-mono text-xs text-ink"><PrivateValue>{fmtCurrency(evt._avgPrice || evt.price)}</PrivateValue></span>
                </div>
              </div>
            </div>
          )}

          {evt.type === 'stop' && (
            <div>
              <div className="font-mono text-[9px] tracking-widest uppercase text-inkMuted mb-2">Stop Details</div>
              <div className="space-y-1">
                {evt.previousPrice != null && (
                  <div className="flex justify-between">
                    <span className="font-mono text-xs text-inkMuted">Previous</span>
                    <span className="font-mono text-xs text-inkMuted"><PrivateValue>{fmtCurrency(evt.previousPrice)}</PrivateValue></span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="font-mono text-xs text-inkMuted">New Stop</span>
                  <span className="font-mono text-xs text-ink"><PrivateValue>{fmtCurrency(evt.price)}</PrivateValue></span>
                </div>
              </div>
            </div>
          )}

          {/* Per-account breakdown for consolidated events */}
          {isConsolidated && (
            <div>
              <div className="font-mono text-[9px] tracking-widest uppercase text-inkMuted mb-2">By Account</div>
              <div className="space-y-2">
                {children.map((child, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-canvas2 border border-border">
                    <AccountName accountNumber={child.accountNumber} className="font-mono text-xs text-ink" />
                    <div className="text-right">
                      {child.type === 'execution' && (
                        <span className="font-mono text-xs text-ink tabular-nums">
                          <PrivateValue>{Math.round(Number(child.quantity))} @ {fmtCurrency(child.price)}</PrivateValue>
                        </span>
                      )}
                      {child.type === 'stop' && (
                        <span className="font-mono text-xs text-ink tabular-nums">
                          <PrivateValue>{child.previousPrice != null ? `${fmtCurrency(child.previousPrice)} → ` : ''}{fmtCurrency(child.price)}</PrivateValue>
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Single event account */}
          {!isConsolidated && evt.accountNumber && (
            <div>
              <div className="font-mono text-[9px] tracking-widest uppercase text-inkMuted mb-1">Account</div>
              <AccountName accountNumber={evt.accountNumber} className="font-mono text-sm font-semibold text-ink" />
            </div>
          )}

          {/* Note section */}
          <div className="border-t border-border pt-4">
            <div className="font-mono text-[9px] tracking-widest uppercase text-inkMuted mb-2">Note</div>

            {evt.note && !editingNote ? (
              <div>
                <div className="font-mono text-xs text-ink whitespace-pre-wrap mb-2">{evt.note}</div>
                <button
                  onClick={() => { setEditingNote(true); setNoteText(evt.note) }}
                  className="font-mono text-[10px] text-inkMuted hover:text-ink transition-colors"
                >
                  Edit
                </button>
              </div>
            ) : (
              <div>
                <textarea
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  placeholder="Add a note about this decision..."
                  rows={3}
                  className="w-full font-mono text-xs px-3 py-2 rounded-lg bg-canvas2 border border-border text-ink resize-none mb-2"
                  autoFocus={editingNote}
                />
                <button
                  onClick={() => {
                    onSaveNote(evt, noteText.trim(), evt.note ? evt.id : null)
                    setEditingNote(false)
                  }}
                  disabled={!noteText.trim() || saving}
                  className="font-mono text-[10px] px-4 py-1.5 rounded-lg bg-ink text-canvas border border-ink disabled:opacity-40 transition-colors"
                >
                  {saving ? 'Saving...' : 'Save Note'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slideInRight 250ms ease both;
        }
      `}</style>
    </div>,
    document.body
  )
}

/* ── Main Component ───────────────────────────────────────────────────── */

export default function DecisionTimeline({ tradeIds, symbol, onNotesCount, compact = false, onCollapse = null }) {
  const { user } = useAuth()
  const [rawEvents, setRawEvents] = useState([])
  const [loading, setLoading] = useState(() => Boolean(tradeIds?.length))
  const [selectedIdx, setSelectedIdx] = useState(null)
  const [hoverIdx, setHoverIdx] = useState(null)
  const [hoverRect, setHoverRect] = useState(null)
  const [saving, setSaving] = useState(false)
  const [showAddNote, setShowAddNote] = useState(false)
  const [newNoteText, setNewNoteText] = useState('')
  const [containerWidth, setContainerWidth] = useState(0)
  const scrollRef = useRef(null)
  const trackRef = useRef(null)

  // ── Measure the scroll-container's viewport width (not the inner track) ──
  useEffect(() => {
    if (!scrollRef.current) return
    const obs = new ResizeObserver(entries => {
      for (const entry of entries) setContainerWidth(entry.contentRect.width)
    })
    obs.observe(scrollRef.current)
    return () => obs.disconnect()
  }, [rawEvents.length > 0])

  // ── Load & merge all event sources ──
  const loadEvents = useCallback(async () => {
    if (!user || !tradeIds?.length) {
      setRawEvents((events) => (events.length > 0 ? [] : events))
      setLoading(false)
      if (onNotesCount) onNotesCount(0)
      return
    }
    if (rawEvents.length === 0) setLoading(true)

    const uniqueIds = [...new Set(tradeIds)]
    const [{ data: execs }, { data: stops }, { data: notes }, { data: trades }] = await Promise.all([
      supabase
        .from('executions')
        .select('id, trade_id, account_number, execution_type, side, quantity, price, executed_at')
        .in('trade_id', uniqueIds)
        .eq('user_id', user.id)
        .order('executed_at', { ascending: true }),
      supabase
        .from('stop_history')
        .select('id, trade_id, account_number, stop_price, previous_stop_price, recorded_at, order_id')
        .in('trade_id', uniqueIds)
        .eq('user_id', user.id)
        .order('recorded_at', { ascending: true }),
      supabase
        .from('trade_events')
        .select('id, trade_id, account_number, event_type, price, previous_price, timestamp, note')
        .in('trade_id', uniqueIds)
        .eq('user_id', user.id)
        .order('timestamp', { ascending: true }),
      supabase
        .from('trades')
        .select('id, account_number, quantity_open, quantity_total')
        .in('id', uniqueIds),
    ])

    // Build lookup: account_number → shares for enriching stops
    const tradeQtyMap = {}
    for (const t of (trades || [])) {
      tradeQtyMap[t.account_number] = Number(t.quantity_open) || Number(t.quantity_total) || 0
    }

    const merged = []

    for (const e of (execs || [])) {
      merged.push({
        id: e.id, source: 'execution', tradeId: e.trade_id, type: 'execution',
        side: e.side, accountNumber: e.account_number, quantity: e.quantity,
        price: e.price, timestamp: e.executed_at, executionType: e.execution_type,
      })
    }

    for (const s of (stops || [])) {
      merged.push({
        id: s.id, source: 'stop_history', tradeId: s.trade_id, type: 'stop',
        accountNumber: s.account_number, price: s.stop_price,
        previousPrice: s.previous_stop_price, timestamp: s.recorded_at, orderId: s.order_id,
        quantity: tradeQtyMap[s.account_number] || null,
      })
    }

    for (const n of (notes || [])) {
      merged.push({
        id: n.id, source: 'trade_event', tradeId: n.trade_id,
        type: n.event_type === 'note' ? 'note' : n.event_type === 'stop_moved' ? 'stop' : n.event_type,
        side: n.event_type === 'note' ? null : undefined,
        accountNumber: n.account_number, price: n.price,
        previousPrice: n.previous_price, timestamp: n.timestamp, note: n.note,
      })
    }

    merged.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    setRawEvents(merged)
    setLoading(false)

    const uniqueNotesCount = merged.filter(e => e.note || e.type === 'note').length
    if (onNotesCount) onNotesCount(uniqueNotesCount)
  }, [user, JSON.stringify(tradeIds), onNotesCount])

  useEffect(() => { loadEvents() }, [loadEvents])

  // ── Consolidate events then compute positions ──
  const events = useMemo(() => consolidateEvents(rawEvents), [rawEvents])
  const positioned = useMemo(() => computePositions(events, containerWidth), [events, containerWidth])

  // ── Date labels ──
  const dateLabels = useMemo(() => {
    const seen = new Set()
    const labels = []
    for (const p of positioned) {
      const key = fmtDateShort(p.timestamp)
      if (!seen.has(key)) {
        seen.add(key)
        labels.push({ label: key, px: p.px })
      }
    }
    return labels
  }, [positioned])

  // ── Save note ──
  const handleSaveNote = useCallback(async (evt, text, existingId) => {
    if (!text || !user) return
    setSaving(true)

    if (existingId) {
      await supabase.from('trade_events').update({ note: text }).eq('id', existingId)
    } else {
      await supabase.from('trade_events').insert({
        trade_id: evt.tradeId, user_id: user.id, event_type: 'note',
        price: evt.price || null, previous_price: null,
        account_number: evt.accountNumber || null,
        timestamp: evt.timestamp, note: text,
      })
    }

    setSaving(false)
    setSelectedIdx(null)
    await loadEvents()
  }, [user, loadEvents])

  // ── Add standalone note ──
  const addStandaloneNote = useCallback(async () => {
    if (!newNoteText.trim() || !user || !tradeIds?.length) return
    setSaving(true)

    await supabase.from('trade_events').insert({
      trade_id: tradeIds[0], user_id: user.id, event_type: 'note',
      price: null, previous_price: null, account_number: null,
      timestamp: new Date().toISOString(), note: newNoteText.trim(),
    })

    setSaving(false)
    setNewNoteText('')
    setShowAddNote(false)
    await loadEvents()
  }, [newNoteText, user, tradeIds, loadEvents])

  if (loading) {
    return (
      <div className={`bg-canvas border border-border ${compact ? 'rounded-lg px-3 py-2' : 'rounded-xl px-6 py-4'}`}>
        <div className="font-mono text-xs text-inkMuted animate-pulse text-center">Loading timeline...</div>
      </div>
    )
  }

  const selectedEvt = selectedIdx != null ? positioned[selectedIdx] : null
  const hoverEvt = hoverIdx != null && hoverIdx !== selectedIdx ? positioned[hoverIdx] : null

  const totalWidth = positioned.length > 0 ? positioned[0]._totalWidth : containerWidth
  const scrollable = totalWidth > containerWidth
  const hasLinkedTrades = Boolean(tradeIds?.length)

  return (
    <div
      className={`bg-canvas border border-border ${compact ? 'rounded-lg' : 'rounded-xl'} overflow-hidden`}
      data-testid="decision-timeline"
      data-compact={compact ? 'true' : 'false'}
    >
      {/* Header */}
      <div className={`${compact ? 'px-3 py-1.5' : 'px-6 pt-2 pb-1'} flex items-center justify-between border-b border-border/70`}>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[9px] tracking-widest uppercase text-inkMuted">Decision Timeline</span>
          {scrollable && (
            <span className="font-mono text-[8px] text-inkDim">{'\u2190'} scroll {'\u2192'}</span>
          )}
          <div className="relative group">
            <Info size={10} className="text-inkDim cursor-help" />
            <div className="absolute left-0 top-5 z-50 hidden group-hover:flex items-center gap-3 bg-canvas border border-border rounded-lg shadow-lg px-3 py-1.5 whitespace-nowrap">
              <div className="flex items-center gap-1">
                <ArrowUpCircle size={8} strokeWidth={2.5} className="text-forest" />
                <span className="font-mono text-[8px] text-inkDim">Buy</span>
              </div>
              <div className="flex items-center gap-1">
                <ArrowDownCircle size={8} strokeWidth={2.5} className="text-ember" />
                <span className="font-mono text-[8px] text-inkDim">Sell</span>
              </div>
              <div className="flex items-center gap-1">
                <ShieldCheck size={8} strokeWidth={2.5} className="text-[#c97f2a]" />
                <span className="font-mono text-[8px] text-inkDim">Stop Added</span>
              </div>
              <div className="flex items-center gap-1">
                <Shield size={8} strokeWidth={2.5} className="text-[#c97f2a]" />
                <span className="font-mono text-[8px] text-inkDim">Stop Moved</span>
              </div>
              <div className="flex items-center gap-1">
                <MessageSquare size={8} strokeWidth={2.5} className="text-inkMuted" />
                <span className="font-mono text-[8px] text-inkDim">Note</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {hasLinkedTrades && (
            <button
              onClick={() => setShowAddNote(!showAddNote)}
              className="flex items-center gap-1 font-mono text-[10px] px-2.5 py-1 rounded-full border border-border text-inkMuted hover:text-ink hover:border-ink/30 transition-colors"
            >
              <Plus size={10} />
              Add Note
            </button>
          )}
          {typeof onCollapse === 'function' && (
            <button
              type="button"
              onClick={onCollapse}
              className="p-1 rounded text-inkMuted hover:bg-canvas2 hover:text-ink transition-colors"
              aria-label="Collapse decision timeline"
              data-testid="position-workspace-lab-timeline-toggle"
            >
              <ChevronDown size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Add standalone note form */}
      {showAddNote && hasLinkedTrades && (
        <div className={`${compact ? 'px-3' : 'px-6'} pb-3 pt-2`}>
          <div className="flex gap-2">
            <textarea
              value={newNoteText}
              onChange={e => setNewNoteText(e.target.value)}
              placeholder="Add a note about this position..."
              rows={2}
              className="flex-1 font-mono text-xs px-3 py-2 rounded-lg bg-canvas2 border border-border text-ink resize-none"
            />
            <div className="flex flex-col gap-1">
              <button
                onClick={addStandaloneNote}
                disabled={!newNoteText.trim() || saving}
                className="font-mono text-[10px] px-3 py-1.5 rounded-lg bg-ink text-canvas border border-ink disabled:opacity-40 transition-colors"
              >
                Save
              </button>
              <button
                onClick={() => { setShowAddNote(false); setNewNoteText('') }}
                className="font-mono text-[10px] px-3 py-1.5 rounded-lg border border-border text-inkMuted hover:text-ink transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Timeline track */}
      <div ref={scrollRef} className={`${compact ? 'px-4 pb-1 pt-1.5' : 'px-6 pb-2 pt-2'} overflow-x-auto hide-scrollbar`} style={{ scrollbarWidth: 'thin' }}>
        <div
          ref={trackRef}
          className="relative"
          style={{ height: compact ? 34 : 40, width: scrollable ? totalWidth : '100%', minWidth: scrollable ? totalWidth : undefined }}
        >
          {/* Horizontal line — spans from first to last dot */}
          <div
            className="absolute bg-border"
            style={{
              left: positioned.length > 0 ? positioned[0].px : EDGE_PAD,
              width: positioned.length > 1
                ? positioned[positioned.length - 1].px - positioned[0].px
                : Math.max(0, totalWidth - 2 * EDGE_PAD),
              top: DOT_SIZE / 2,
              height: 1,
              zIndex: 0,
            }}
          />

          {events.length === 0 && (
            <div
              className="absolute font-mono text-[9px] text-inkDim"
              style={{ left: EDGE_PAD, top: DOT_SIZE + 6 }}
            >
              No decisions yet
            </div>
          )}

          {/* Event icons */}
          {positioned.map((evt, i) => {
            const colors = getEventColor(evt)
            const isSelected = selectedIdx === i
            const hasNote = !!evt.note || evt.type === 'note'
            const isConsolidated = evt._consolidated
            const Icon = getEventIcon(evt)

            return (
              <div
                key={`${evt.source}-${evt.id}-${i}`}
                className="absolute"
                style={{ left: evt.px, top: 0, transform: 'translateX(-50%)', zIndex: isSelected ? 3 : 1, willChange: 'transform' }}
              >
                <button
                  onMouseEnter={(e) => {
                    setHoverIdx(i)
                    setHoverRect(e.currentTarget.getBoundingClientRect())
                  }}
                  onMouseLeave={() => { setHoverIdx(null); setHoverRect(null) }}
                  onClick={() => {
                    setSelectedIdx(isSelected ? null : i)
                    setHoverIdx(null)
                    setHoverRect(null)
                  }}
                  className={`relative flex items-center justify-center rounded-full transition-all duration-150
                    hover:scale-[1.25] hover:shadow-md
                    ${isSelected ? `scale-[1.2] ring-2 ${colors.ring} shadow-md` : ''}
                    ${hasNote ? 'animate-note-pulse' : ''}`}
                  style={{ width: DOT_SIZE, height: DOT_SIZE }}
                >
                  <Icon
                    size={14}
                    strokeWidth={2}
                    className={`${
                      evt.type === 'note' ? 'text-inkMuted' :
                      evt.type === 'stop' ? 'text-[#c97f2a]' :
                      evt.side === 'buy' ? 'text-forest' :
                      'text-ember'
                    } drop-shadow-sm`}
                  />
                </button>
                {isConsolidated && (
                  <div
                    className="absolute -top-1.5 -right-2 font-mono text-[7px] font-bold leading-none bg-canvas border border-border rounded-full flex items-center justify-center pointer-events-none"
                    style={{ width: 14, height: 14 }}
                  >
                    {evt._count}
                  </div>
                )}
              </div>
            )
          })}

          {/* Date labels */}
          {dateLabels.map((dl, i) => (
            <div
              key={i}
              className="absolute font-mono text-[8px] text-inkDim whitespace-nowrap"
              style={{ left: dl.px, top: DOT_SIZE + 6, transform: 'translateX(-50%)' }}
            >
              {dl.label}
            </div>
          ))}
        </div>

      </div>

      {/* Hover tooltip */}
      {hoverEvt && hoverRect && <HoverTooltip evt={hoverEvt} anchorRect={hoverRect} isOption={isOCCSymbol(symbol)} />}

      {/* Click slide-over panel */}
      {selectedEvt && (
        <EventDetailPanel
          evt={selectedEvt}
          currentIdx={selectedIdx}
          totalCount={positioned.length}
          onClose={() => setSelectedIdx(null)}
          onNavigate={setSelectedIdx}
          onSaveNote={handleSaveNote}
          saving={saving}
          isOption={isOCCSymbol(symbol)}
        />
      )}

      {/* Animations */}
      <style>{`
        @keyframes notePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(138, 146, 160, 0.3); }
          50% { box-shadow: 0 0 0 4px rgba(138, 146, 160, 0); }
        }
        .animate-note-pulse {
          animation: notePulse 2.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  )
}

/* ── Exported: Notes list for the Notes tab ──────────────────────────────── */

export function NotesList({ tradeIds }) {
  const { user } = useAuth()
  const [notes, setNotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [newNoteText, setNewNoteText] = useState('')
  const [saving, setSaving] = useState(false)

  const loadNotes = useCallback(async () => {
    if (!user || !tradeIds?.length) { setLoading(false); return }
    if (notes.length === 0) setLoading(true)

    const uniqueIds = [...new Set(tradeIds)]
    const { data } = await supabase
      .from('trade_events')
      .select('id, trade_id, account_number, event_type, price, previous_price, timestamp, note')
      .in('trade_id', uniqueIds)
      .eq('user_id', user.id)
      .not('note', 'is', null)
      .order('timestamp', { ascending: false })

    setNotes(data || [])
    setLoading(false)
  }, [user, JSON.stringify(tradeIds)])

  useEffect(() => { loadNotes() }, [loadNotes])

  const addNote = useCallback(async () => {
    if (!newNoteText.trim() || !user || !tradeIds?.length) return
    setSaving(true)

    await supabase.from('trade_events').insert({
      trade_id: tradeIds[0], user_id: user.id, event_type: 'note',
      price: null, previous_price: null, account_number: null,
      timestamp: new Date().toISOString(), note: newNoteText.trim(),
    })

    setSaving(false)
    setNewNoteText('')
    await loadNotes()
  }, [newNoteText, user, tradeIds, loadNotes])

  if (loading) {
    return <div className="font-mono text-xs text-inkMuted animate-pulse text-center py-4">Loading notes...</div>
  }

  function eventContext(n) {
    if (n.event_type === 'note' && !n.price) return 'Standalone note'
    if (n.event_type === 'note' && n.price) return `Note at ${fmtCurrency(n.price)}`
    if (n.event_type === 'stop_moved') return `Stop: ${fmtCurrency(n.previous_price)} → ${fmtCurrency(n.price)}`
    return n.event_type
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <textarea
          value={newNoteText}
          onChange={e => setNewNoteText(e.target.value)}
          placeholder="Add a note..."
          rows={2}
          className="flex-1 font-mono text-xs px-3 py-2 rounded-lg bg-canvas2 border border-border text-ink resize-none"
        />
        <button
          onClick={addNote}
          disabled={!newNoteText.trim() || saving}
          className="self-end font-mono text-[10px] px-4 py-2 rounded-lg bg-ink text-canvas border border-ink disabled:opacity-40 transition-colors"
        >
          {saving ? 'Saving...' : 'Add Note'}
        </button>
      </div>

      {notes.length === 0 ? (
        <div className="font-mono text-xs text-inkMuted text-center py-4">
          No notes yet. Add a note above or click an event on the timeline.
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map(n => (
            <div key={n.id} className="border border-border rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="font-mono text-[10px] text-inkMuted">{fmtTimeCT(n.timestamp)}</span>
                <span className="font-mono text-[9px] px-2 py-0.5 rounded-full bg-canvas2 text-inkMuted border border-border">
                  {eventContext(n)}
                </span>
                {n.account_number && (
                  <AccountName accountNumber={n.account_number} className="font-mono text-[10px] text-inkMuted" />
                )}
              </div>
              <div className="font-mono text-xs text-ink whitespace-pre-wrap">{n.note}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
